import React, { useState, useCallback } from 'react';
import { useScheme } from '@/context/SchemeContext';
import { useUsersQuery, useFilterOptions } from './useUsersQuery';
import UsersFilters from './UsersFilters';
import UsersTable from './UsersTable';
import ExportModal from './ExportModal';
import UserDetailsModal from './UserDetailsModal';
import ResendBroadcastModal from './ResendBroadcastModal';
import NoSchemeSelected from '@/components/NoSchemeSelected';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';

const { FiSearch, FiDownload, FiRefreshCw, FiSend } = FiIcons;

const INITIAL_FILTERS = {
  filterStatus: '',
  filterState: '',
  filterGift: '',
  filterSlab: '',
  dateFrom: '',
  dateTo: '',
};

const Users = () => {
  const { selectedSchemeId, selectedScheme } = useScheme();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isBroadcastModalOpen, setIsBroadcastModalOpen] = useState(false);
  const [selectedUserMobile, setSelectedUserMobile] = useState(null);

  const perPage = 20;
  const isSearchActive = search.trim().length > 0;

  const { data: filterOptions } = useFilterOptions(selectedSchemeId);
  
  const { data, isLoading, isFetching, refetch } = useUsersQuery({
    schemeId: selectedSchemeId,
    page,
    search,
    perPage,
    ...filters,
  });

  const users = data?.data || [];
  const totalPages = data?.total_pages || 1;
  const totalRecords = data?.total_records || (isSearchActive ? users.length : 0);

  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    setSearch('');
    setFilters(INITIAL_FILTERS);
    setPage(1);
  }, []);

  if (!selectedSchemeId) return <NoSchemeSelected />;

  const activeFiltersForExport = {
    q: search,
    gift: filters.filterGift,
    state: filters.filterState,
    status: filters.filterStatus,
    slab: filters.filterSlab,
    date_from: filters.dateFrom,
    date_to: filters.dateTo,
  };

  return (
    <div className="space-y-6 pb-20 animate-fade-up">
      <div className="flex flex-col xl:flex-row gap-4">
        <div className="relative flex-1 group">
          <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400 group-focus-within:text-primary-500 transition-colors" />
          <Input 
            placeholder="Search by Outlet Name, Mobile Number or Pincode..." 
            className="pl-12 h-14 bg-white border-none shadow-sm rounded-[1.25rem] text-sm focus:ring-4 focus:ring-primary-100"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => setIsBroadcastModalOpen(true)}
            variant="outline"
            className="h-14 px-6 rounded-[1.25rem] border-primary-100 bg-primary-50 text-primary-700 hover:bg-primary-100 shadow-sm flex items-center transition-all active:scale-95"
          >
            <SafeIcon icon={FiSend} className="w-4 h-4 mr-2" />
            <span className="text-[10px] font-black uppercase tracking-widest">Resend Broadcast</span>
          </Button>
          <Button 
            onClick={() => setIsExportModalOpen(true)}
            disabled={users.length === 0}
            className="h-14 px-8 rounded-[1.25rem] bg-surface-900 hover:bg-black text-white shadow-xl flex items-center"
          >
            <SafeIcon icon={FiDownload} className="w-4 h-4 mr-2" />
            <span className="text-xs font-black uppercase tracking-widest">Export Result</span>
          </Button>
          <Button 
            variant="outline" 
            size="icon" 
            onClick={() => refetch()} 
            disabled={isFetching}
            className="h-14 w-14 rounded-[1.25rem] border-white bg-white shadow-sm"
          >
            <SafeIcon icon={FiRefreshCw} className={`w-4 h-4 text-primary-600 ${isFetching ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      <UsersFilters 
        filters={filters} 
        filterOptions={filterOptions}
        isSearchActive={isSearchActive}
        onChange={handleFilterChange}
        onReset={resetFilters}
      />

      <UsersTable 
        users={users} 
        isLoading={isLoading} 
        isFetching={isFetching}
        totalRecords={totalRecords}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        onRowClick={setSelectedUserMobile}
      />

      <UserDetailsModal 
        mobileNo={selectedUserMobile} 
        onClose={() => setSelectedUserMobile(null)} 
        schemeId={selectedSchemeId} 
      />

      <ExportModal 
        isOpen={isExportModalOpen} 
        onClose={() => setIsExportModalOpen(false)} 
        totalRecords={totalRecords}
        activeFilters={activeFiltersForExport}
        schemeId={selectedSchemeId}
      />

      <ResendBroadcastModal
        isOpen={isBroadcastModalOpen}
        onClose={() => setIsBroadcastModalOpen(false)}
        schemeId={selectedSchemeId}
        schemeName={selectedScheme?.name || "Target Scheme"}
      />
    </div>
  );
};

export default Users;